import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ContributionPersonRef } from '@gwc/shared';
import { membersApi } from '../api/members';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

/**
 * "Who?" — type a name; pick someone from the records, or keep the free text.
 * Free text is fine: the reviewer resolves it (or a non-member row is created)
 * at merge time. Searches EVERY person, so decades of imported names count.
 */
export function PersonPicker({
  value,
  onChange,
  placeholder = 'Name',
  className = 'field',
  disabled,
}: {
  value: ContributionPersonRef | null | undefined;
  onChange: (v: ContributionPersonRef | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const id = useId();
  const [text, setText] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();
  const q = useDebouncedValue(text, 250);
  useEffect(() => setText(value?.name ?? ''), [value?.name]);

  const { data: matches = [] } = useQuery({
    queryKey: ['people-lookup', q],
    queryFn: () => membersApi.lookup(q, 8),
    enabled: open && q.trim().length >= 2,
    staleTime: 30_000,
  });

  const pick = (p: { id: string; firstName: string; lastName: string }) => {
    const name = `${p.firstName} ${p.lastName}`.trim();
    setText(name);
    onChange({ personId: p.id, name });
    setOpen(false);
  };
  const setFree = (t: string) => {
    setText(t);
    onChange(t.trim() ? { personId: null, name: t } : null);
    setHighlight(0);
    setOpen(true);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input
          id={id}
          className={className}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={`${id}-list`}
          onChange={(e) => setFree(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={(e) => {
            if (!open || !matches.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, matches.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const m = matches[highlight];
              if (m) pick(m);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {value?.name && !value.personId && (
          <span
            className="shrink-0 rounded-full border border-ink-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-cream-300/50"
            title="Not matched to anyone in the records yet — that's fine; a Records Admin sorts it out on merge."
          >
            new name
          </span>
        )}
      </div>
      {open && matches.length > 0 && (
        <ul
          id={`${id}-list`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-ink-800 bg-ink-950 py-1 text-sm shadow-lg"
        >
          {matches.map((m, i) => (
            <li
              key={m.id}
              role="option"
              aria-selected={i === highlight}
              className={`cursor-pointer px-3 py-1.5 ${i === highlight ? 'bg-ink-800 text-cream-50' : 'text-cream-200'}`}
              onMouseDown={(e) => {
                e.preventDefault();
                clearTimeout(blurTimer.current);
                pick(m);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              {m.firstName} {m.lastName}
              <span className="ml-2 text-xs text-cream-300/50">{m.isMember ? 'member' : 'in the records'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
